import React from 'react';
import { useTranslation } from 'react-i18next';
import { JOB_PORTAL_KEYS, NS } from '../../../../../i18n/constants/keys';
import { Button } from '../../../../../ui';

type ShareSectionProps = {
  jobPostData: any;
  mailtoUrl: string;
  copyToClipboard: () => void;
  shareOnLinkedIn: () => void;
  shareOnWhatsApp: () => void;
  shareOnFacebook: () => void;
  shareOnX: () => void;
};

export const ShareSection = ({
  jobPostData,
  mailtoUrl,
  copyToClipboard,
  shareOnLinkedIn,
  shareOnWhatsApp,
  shareOnFacebook,
  shareOnX,
}: ShareSectionProps) => {
  const { t } = useTranslation(NS.jobPortal);

  return (
    <div>
      <div className='mb-4 text-center'>
        <h3 className='mb-1 text-base font-extrabold text-slate-900'>
          { t(JOB_PORTAL_KEYS.share.title) }
        </h3>
        <p className='text-xs text-slate-500'>
          { t(JOB_PORTAL_KEYS.share.subtitle) }
        </p>
      </div>

      <div className='space-y-3'>
        { jobPostData?.id && (
          <div className='relative group'>
            <div className='flex items-stretch overflow-hidden rounded-lg border border-emerald-200 bg-emerald-50 shadow-sm'>
              <div className='flex min-w-0 flex-1 items-center bg-white/80 px-3 py-2'>
                <svg
                    className='mr-2 h-4 w-4 flex-shrink-0 text-emerald-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
                  />
                </svg>
                <input
                    type='text'
                    value={`${window.location.origin}/jobs/${jobPostData.id}`}
                    readOnly
                    className='min-w-0 flex-1 bg-transparent font-mono text-xs text-slate-700 focus:outline-none'
                />
              </div>
            </div>
          </div>
        ) }

        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
          <Button
              label={t(JOB_PORTAL_KEYS.share.copyUrl)}
              hasIcon
              className='w-full'
              variant='secondary'
              onClick={copyToClipboard}
              icon={
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' />
                </svg>
              }
          />
          <Button
              label={t(JOB_PORTAL_KEYS.share.emailFriend)}
              hasIcon
              variant='primary'
              onClick={() => { if (mailtoUrl) window.location.href = mailtoUrl; }}
              className='w-full'
              icon={
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 8l7.89 3.26a2 2 0 001.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
                </svg>
              }
          />
        </div>

        <div className='border-t border-gray-100 pt-4'>
          <p className='mb-2 text-center text-[11px] font-bold uppercase text-slate-500'>
            { t(JOB_PORTAL_KEYS.share.socialNetworks) }
          </p>
          <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
            <button
                onClick={shareOnLinkedIn}
                className='group flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 transition hover:border-blue-300 hover:bg-blue-50'
            >
              <svg
                  className='w-5 h-5 text-blue-600 group-hover:scale-110 transition-transform'
                  fill='currentColor'
                  viewBox='0 0 24 24'
              >
                <path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' />
              </svg>
              <span className='text-xs font-semibold text-slate-700 group-hover:text-blue-600'>
                LinkedIn
              </span>
            </button>
            <button
                onClick={shareOnWhatsApp}
                className='group flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 transition hover:border-green-300 hover:bg-green-50'
            >
              <svg
                  className='w-5 h-5 text-green-600 group-hover:scale-110 transition-transform'
                  fill='currentColor'
                  viewBox='0 0 24 24'
              >
                <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488' />
              </svg>
              <span className='text-xs font-semibold text-slate-700 group-hover:text-green-600'>
                WhatsApp
              </span>
            </button>
            <button
                onClick={shareOnFacebook}
                className='group flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 transition hover:border-blue-300 hover:bg-blue-50'
            >
              <svg
                  className='w-5 h-5 text-blue-600 group-hover:scale-110 transition-transform'
                  fill='currentColor'
                  viewBox='0 0 24 24'
              >
                <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
              </svg>
              <span className='text-xs font-semibold text-slate-700 group-hover:text-blue-600'>
                Facebook
              </span>
            </button>
            <button
                onClick={shareOnX}
                className='group flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 transition hover:border-slate-300 hover:bg-slate-50'
            >
              <svg
                  className='w-5 h-5 text-gray-700 group-hover:scale-110 transition-transform'
                  fill='currentColor'
                  viewBox='0 0 24 24'
              >
                <path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
              </svg>
              <span className='text-xs font-semibold text-slate-700 group-hover:text-slate-600'>
                X
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
